import { defineStore } from 'pinia';
import { getSubdomainFromHostname } from '@/utils/tenantUtils';
import { resolveSubdomainToTenantId, validateTenant } from '@/services/tenantService';
import type { TenantErrorCode } from '@/types/tenant';

export type TenantValidationStatus = 'pending' | 'valid' | 'invalid';

/** Re-export for consumers. */
export type { TenantErrorCode };

interface TenantState {
  /** Normalized subdomain slug (e.g. "demo", "cat"). */
  subdomain: string;
  /** Tenant UUID for X-Tenant-ID header. */
  tenantId: string | null;
  /** Bootstrap/validation state. */
  validationStatus: TenantValidationStatus;
  /** Set when validation fails or a tenant 400/403 is received. */
  errorCode: TenantErrorCode | null;
}

export const useTenantStore = defineStore('tenant', {
  state: (): TenantState => ({
    subdomain: 'demo',
    tenantId: null,
    validationStatus: 'pending',
    errorCode: null,
  }),

  getters: {
    /** Normalized tenant slug (subdomain). */
    tenantSlug: (state): string => state.subdomain,

    /** Current tenant UUID; null if not resolved. */
    currentTenantId: (state): string | null => state.tenantId,

    isTenantValid: (state): boolean => state.validationStatus === 'valid',

    isTenantInvalid: (state): boolean => state.validationStatus === 'invalid',

    isTenantPending: (state): boolean => state.validationStatus === 'pending',

    tenantErrorCode: (state): TenantErrorCode | null => state.errorCode,
  },

  actions: {
    /**
     * Initialize tenant from current hostname.
     * Call once at app startup (browser only).
     */
    initFromHostname() {
      if (typeof window === 'undefined') {
        this.subdomain = 'demo';
        return;
      }
      this.subdomain = getSubdomainFromHostname(window.location.hostname);
    },

    setResolvedTenantId(tenantId: string | null) {
      this.tenantId = tenantId;
    },

    setValid() {
      this.validationStatus = 'valid';
      this.errorCode = null;
    },

    setInvalid(errorCode: TenantErrorCode) {
      this.validationStatus = 'invalid';
      this.errorCode = errorCode;
    },

    setPending() {
      this.validationStatus = 'pending';
      this.errorCode = null;
    },

    /** Reset for tests or re-validation. */
    reset() {
      this.subdomain = 'demo';
      this.tenantId = null;
      this.validationStatus = 'pending';
      this.errorCode = null;
    },

    /**
     * Bootstrap: detect subdomain, resolve to UUID, validate tenant with backend.
     * Call once at app startup after Pinia is installed.
     */
    async bootstrap(): Promise<void> {
      this.setPending();
      this.initFromHostname();

      const tenantId = await resolveSubdomainToTenantId(this.subdomain);
      this.setResolvedTenantId(tenantId);

      if (!tenantId) {
        this.setInvalid('tenant_config');
        return;
      }

      const { valid, errorCode } = await validateTenant(tenantId);
      if (valid) {
        this.setValid();
      } else {
        this.setInvalid(errorCode ?? 'tenant_config');
      }
    },
  },
});
